import {
  Building2,
  DollarSign,
  TrendingDown,
  TrendingUp,
  Users,
} from "@ews-admin/global-design-system";
import { Select } from "../../components/atoms/Select";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Area,
  AreaChart,
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { adminStatsApi } from "../../services/adminStats.api";
import {
  AdminAnalyticsStats,
  AnalyticsPeriod,
  RevenueTrendPoint,
} from "../../types/adminStats.definition";
import { CHART_COLORS } from "../../utils/chart-utils";
import { formatMoney } from "../../utils/money-utils";
import {
  formatDayShort,
  formatMonthShort,
} from "../../utils/date-format-utils";

const PERIODS: { value: AnalyticsPeriod; labelKey: string }[] = [
  { value: "7d", labelKey: "admin.analytics.periods.last7Days" },
  { value: "30d", labelKey: "admin.analytics.periods.last30Days" },
  { value: "90d", labelKey: "admin.analytics.periods.last90Days" },
  { value: "1y", labelKey: "admin.analytics.periods.lastYear" },
];

const ANIMATION_MS = 900;

/** Stable empty series — see the memo on `revenueTrend` (MED-1541). */
const NO_TREND: RevenueTrendPoint[] = [];

/**
 * MED-1480: the most ticks the revenue trend axis carries. 90 days is 90 buckets, which
 * overprints into an unreadable smear, so every nth label is drawn instead.
 */
const MAX_TREND_TICKS = 12;

/** Facility names longer than this are shortened on the chart axis (the table shows them in full). */
const AXIS_NAME_LENGTH = 16;

/** "+12.5%" or "-3%"; null when there is no base to compare with. */
const formatGrowth = (growth: number | null) =>
  growth === null ? null : `${growth > 0 ? "+" : ""}${growth}%`;

const shortName = (name: string) =>
  name.length > AXIS_NAME_LENGTH
    ? `${name.slice(0, AXIS_NAME_LENGTH - 1)}…`
    : name;

interface GrowthLineProps {
  growth: number | null;
  label: string;
}

/** Signed growth with an up or down arrow; nothing when the growth is unknown. */
const GrowthLine: React.FC<GrowthLineProps> = ({ growth, label }) => {
  if (growth === null) {
    return null;
  }
  const color = growth >= 0 ? "text-green-500" : "text-red-500";
  const Icon = growth >= 0 ? TrendingUp : TrendingDown;
  return (
    <div className="flex items-center mt-2 text-sm">
      <Icon className={`mr-1 w-4 h-4 ${color}`} />
      <span className={`font-medium ${color}`}>{formatGrowth(growth)}</span>
      <span className="ml-1 text-gray-500">{label}</span>
    </div>
  );
};

export const AdminAnalytics: React.FC = () => {
  const { t, i18n } = useTranslation();
  // MED-1480: the last 30 days by default. "1y" was the widest view, but it is also the
  // only period bucketed by month, so the page opened on the least actionable picture of
  // a business the admin follows day to day; 12 months stays one click away.
  const [period, setPeriod] = useState<AnalyticsPeriod>("30d");
  const [stats, setStats] = useState<AdminAnalyticsStats | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);

  // MED-1336: every figure comes from the admin analytics stats of the selected period.
  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setLoadError(false);
    adminStatsApi
      .getAnalytics(period)
      .then(data => {
        if (!cancelled) {
          setStats(data);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setStats(null);
          setLoadError(true);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [period]);

  const activeShare =
    stats && stats.clientFacilitiesTotal > 0
      ? Math.round(
          (stats.clientFacilitiesActive / stats.clientFacilitiesTotal) * 100
        )
      : null;

  const revenueLabel = t("admin.analytics.revenue");
  const patientsLabel = t("admin.analytics.patients");
  const compactAmount = (value: number) =>
    new Intl.NumberFormat(i18n.language, { notation: "compact" }).format(value);
  // MED-1480: the backend answers `revenueTrend` + `trendGranularity` for the selected
  // period; the page used to map the removed fixed six-month `monthlyRevenue`, which
  // silently rendered an empty chart. Labels follow the granularity: a day for
  // 7d/30d/90d, a month for 1y.
  // `stats.revenueTrend` is the array the response carried, so it only changes
  // when new stats arrive; NO_TREND keeps that true before the first load too.
  const trendPoints = stats?.revenueTrend ?? NO_TREND;
  const byDay = stats?.trendGranularity === "DAY";
  // MED-1541: memoised, so the array keeps its identity across re-renders.
  // Recharts restarts an Area's entry animation whenever `data` is a new object,
  // and the animation starts flat on the baseline. ResponsiveContainer re-renders
  // the chart on every measurement it takes — fonts settling, the sibling charts
  // mounting, a resize — so a series rebuilt on each render could be sent back to
  // the baseline over and over and never finish growing, which is a revenue chart
  // that reads as flat (or as one small bump) while its axis shows the real scale.
  const revenueTrend = React.useMemo(
    () =>
      trendPoints.map(point => ({
        bucket: point.bucket,
        label: byDay
          ? formatDayShort(point.bucket, i18n.language)
          : formatMonthShort(point.bucket, i18n.language),
        revenue: point.revenue,
      })),
    [trendPoints, byDay, i18n.language]
  );
  // Recharts' `interval` is the number of ticks to SKIP between two drawn ones.
  const tickInterval = Math.max(
    0,
    Math.ceil(revenueTrend.length / MAX_TREND_TICKS) - 1
  );

  const trendOf = (growth: number | null) => {
    if (growth === null) {
      return <span className="text-sm text-gray-500">—</span>;
    }
    if (growth === 0) {
      return (
        <span className="text-sm text-gray-500">
          {t("admin.analytics.stable")}
        </span>
      );
    }
    const growing = growth > 0;
    const Icon = growing ? TrendingUp : TrendingDown;
    return (
      <div className="flex items-center">
        <Icon
          className={`w-4 h-4 ${growing ? "text-green-500" : "text-red-500"}`}
        />
        <span
          className={`ml-1 text-sm ${growing ? "text-green-500" : "text-red-500"}`}
        >
          {growing
            ? t("admin.analytics.growing")
            : t("admin.analytics.declining")}
        </span>
      </div>
    );
  };

  return (
    <div className="page-container">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 page-header">
        <div className="shrink-0">
          <h2 className="whitespace-nowrap text-2xl font-bold text-gray-900">
            {t("admin.analytics.title")}
          </h2>
          <p className="whitespace-nowrap text-gray-600">
            {t("admin.analytics.subtitle")}
          </p>
        </div>
        {/* Fixed width on the right: the full-width Select used to squeeze the title onto two lines */}
        <div className="ml-auto w-56">
          <Select
            value={period}
            onChange={value => setPeriod(value as AnalyticsPeriod)}
            options={PERIODS.map(item => ({
              label: t(item.labelKey),
              value: item.value,
            }))}
          />
        </div>
      </div>

      {loadError && (
        <div
          role="alert"
          className="page-section rounded border border-red-400 bg-red-100 p-3 text-red-700"
        >
          {t("admin.analytics.loadError")}
        </div>
      )}

      {isLoading && !stats && (
        <div className="py-12 text-center text-gray-500">
          {t("common.loading")}
        </div>
      )}

      {stats && (
        <>
          {/* Key Metrics */}
          <div className="grid grid-cols-1 gap-6 page-section md:grid-cols-3">
            <div className="border-l-4 card-container border-ews-primary">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm font-medium text-gray-600">
                    {t("admin.analytics.totalRevenue")}
                  </p>
                  <p className="text-2xl font-semibold text-gray-900">
                    {formatMoney(stats.revenue)}
                  </p>
                </div>
                <div className="flex justify-center items-center w-12 h-12 bg-blue-100 rounded-full">
                  <DollarSign className="w-6 h-6 text-blue-600" />
                </div>
              </div>
              <GrowthLine
                growth={stats.revenueGrowth}
                label={t("admin.analytics.fromPreviousPeriod")}
              />
            </div>

            <div className="border-l-4 card-container border-success-500">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm font-medium text-gray-600">
                    {t("admin.analytics.activeFacilities")}
                  </p>
                  <p className="text-2xl font-semibold text-gray-900">
                    {stats.clientFacilitiesActive}/{stats.clientFacilitiesTotal}
                  </p>
                </div>
                <div className="flex justify-center items-center w-12 h-12 bg-green-100 rounded-full">
                  <Building2 className="w-6 h-6 text-green-600" />
                </div>
              </div>
              {activeShare !== null && (
                <p className="mt-2 text-sm font-medium text-green-500">
                  {t("admin.analytics.activeShare", { percent: activeShare })}
                </p>
              )}
            </div>

            <div className="border-l-4 card-container border-secondary-500">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm font-medium text-gray-600">
                    {t("admin.analytics.totalUsers")}
                  </p>
                  <p className="text-2xl font-semibold text-gray-900">
                    {stats.usersTotal.toLocaleString()}
                  </p>
                </div>
                <div className="flex justify-center items-center w-12 h-12 bg-purple-100 rounded-full">
                  <Users className="w-6 h-6 text-purple-600" />
                </div>
              </div>
              <p className="mt-2 text-sm text-gray-500">
                {t("admin.analytics.newUsers", { count: stats.newUsers })}
              </p>
              <GrowthLine
                growth={stats.newUsersGrowth}
                label={t("admin.analytics.fromPreviousPeriod")}
              />
            </div>
          </div>

          {/* Revenue Trend: animated area over the months */}
          <section
            className="relative page-section card-container"
            aria-label={t("admin.analytics.revenueTrend")}
          >
            <h3 className="mb-4 text-lg font-medium text-gray-900">
              {t("admin.analytics.revenueTrend")}
            </h3>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart
                  data={revenueTrend}
                  margin={{ top: 8, right: 16, bottom: 0, left: 8 }}
                >
                  <defs>
                    <linearGradient
                      id="revenueTrendFill"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop
                        offset="5%"
                        stopColor={CHART_COLORS.primary}
                        stopOpacity={0.45}
                      />
                      <stop
                        offset="95%"
                        stopColor={CHART_COLORS.primary}
                        stopOpacity={0.02}
                      />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis
                    dataKey="label"
                    interval={tickInterval}
                    tickLine={false}
                    fontSize={12}
                  />
                  <YAxis
                    tickFormatter={compactAmount}
                    tickLine={false}
                    fontSize={12}
                    width={56}
                  />
                  <Tooltip formatter={value => formatMoney(Number(value))} />
                  <Area
                    type="monotone"
                    dataKey="revenue"
                    name={revenueLabel}
                    stroke={CHART_COLORS.primary}
                    strokeWidth={3}
                    fill="url(#revenueTrendFill)"
                    activeDot={{ r: 6 }}
                    animationDuration={ANIMATION_MS}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            <ul className="sr-only" data-testid="chart-data">
              {revenueTrend.map(item => (
                <li key={item.bucket}>
                  {`${item.label}: ${formatMoney(item.revenue)}`}
                </li>
              ))}
            </ul>
          </section>

          {/* Facility Performance: revenue bars with the patients line, then the details */}
          <section
            className="relative overflow-hidden bg-white rounded-lg shadow-sm"
            aria-label={t("admin.analytics.facilityPerformance")}
          >
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">
                {t("admin.analytics.facilityPerformance")}
              </h3>
            </div>
            {stats.topFacilities.length === 0 ? (
              /* MED-1541: "no facility had a paid invoice" reads as "nothing was
                 paid", which contradicts the revenue card right above it. Facility
                 invoices are only one source — a doctor licence paid directly is
                 revenue with no facility behind it — so say where the money came
                 from instead of leaving the two cards disagreeing. */
              <p className="px-6 py-12 text-center text-gray-500">
                {stats.revenue > 0
                  ? t("admin.analytics.noFacilityRevenueDespiteRevenue", {
                      revenue: formatMoney(stats.revenue),
                    })
                  : t("admin.analytics.noFacilityRevenue")}
              </p>
            ) : (
              <>
                <div className="h-80 px-6 pt-4">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart
                      data={stats.topFacilities}
                      margin={{ top: 8, right: 8, bottom: 0, left: 8 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis
                        dataKey="name"
                        tickFormatter={shortName}
                        interval={0}
                        tickLine={false}
                        fontSize={12}
                      />
                      <YAxis
                        yAxisId="revenue"
                        tickFormatter={compactAmount}
                        tickLine={false}
                        fontSize={12}
                        width={56}
                      />
                      <YAxis
                        yAxisId="patients"
                        orientation="right"
                        allowDecimals={false}
                        tickLine={false}
                        fontSize={12}
                      />
                      <Tooltip
                        formatter={(value, name) =>
                          name === revenueLabel
                            ? formatMoney(Number(value))
                            : value
                        }
                      />
                      <Legend />
                      <Bar
                        yAxisId="revenue"
                        dataKey="revenue"
                        name={revenueLabel}
                        fill={CHART_COLORS.secondary}
                        radius={[6, 6, 0, 0]}
                        maxBarSize={56}
                        animationDuration={ANIMATION_MS}
                      />
                      <Line
                        yAxisId="patients"
                        type="monotone"
                        dataKey="patients"
                        name={patientsLabel}
                        stroke={CHART_COLORS.amber}
                        strokeWidth={3}
                        dot={{ r: 5 }}
                        animationDuration={ANIMATION_MS}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
                <ul className="sr-only" data-testid="chart-data">
                  {stats.topFacilities.map(facility => (
                    <li key={facility.facilityId}>
                      {`${facility.name}: ${formatMoney(facility.revenue)}, ${facility.patients} ${patientsLabel}`}
                    </li>
                  ))}
                </ul>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                          {t("admin.analytics.facility")}
                        </th>
                        <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                          {revenueLabel}
                        </th>
                        <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                          {patientsLabel}
                        </th>
                        <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                          {t("admin.analytics.growth")}
                        </th>
                        <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                          {t("admin.analytics.trend")}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {stats.topFacilities.map(facility => (
                        <tr
                          key={facility.facilityId}
                          className="hover:bg-gray-50"
                        >
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <Building2 className="mr-3 w-5 h-5 text-gray-400" />
                              <div className="text-sm font-medium text-gray-900">
                                {facility.name}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900 whitespace-nowrap">
                            {formatMoney(facility.revenue)}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900 whitespace-nowrap">
                            {facility.patients}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900 whitespace-nowrap">
                            {formatGrowth(facility.growth) ?? "—"}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {trendOf(facility.growth)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </section>
        </>
      )}
    </div>
  );
};
